import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { 
  Home, Search, User, Menu, X, LogOut, LayoutDashboard, 
  Car, Settings, ShieldCheck, Flame, MessageSquare, Calendar
} from 'lucide-react';
import { useState } from 'react';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import Logo from '../assets/logo.svg';
import FeedbackModal from './FeedbackModal';

export const Layout = () => {
  const { user, logout, isAuthenticated } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [feedbackOpen, setFeedbackOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const getDashboardLink = () => {
    if (user?.role === 'admin') return '/admin';
    // For all users, direct to user dashboard where they can see their businesses
    return '/user-dashboard';
  };

  const isActive = (path) => location.pathname === path;
  const BUILD_VERSION = "2026-03-23-1600";

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      {/* Desktop Header */}
      <header className="hidden md:block fixed top-0 left-0 right-0 z-50 glass">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <img src={Logo} alt="RydQuest" className="h-10 w-auto" />
            <span className="text-xl font-bold tracking-tight text-white">RydQuest</span>
          </Link>

          <nav className="flex items-center gap-8">
            <Link 
              to="/" 
              className={`text-sm font-medium transition-colors hover:text-white ${
                isActive('/') ? 'text-white' : 'text-neutral-400'
              }`}
            >
              Home
            </Link>
            <Link 
              to="/events" 
              className={`text-sm font-medium transition-colors hover:text-white ${
                isActive('/events') ? 'text-white' : 'text-neutral-400'
              }`}
            >
              Events
            </Link>
            <Link 
              to="/sidequests" 
              className={`text-sm font-medium transition-colors hover:text-white ${
                location.pathname.startsWith('/sidequests') ? 'text-white' : 'text-neutral-400'
              }`}
            >
              Sidequests
            </Link>
          </nav>

          <div className="flex items-center gap-4">
            {/* Feedback Button */}
            <button
              onClick={() => setFeedbackOpen(true)}
              className="flex items-center gap-2 text-neutral-400 hover:text-white transition-colors"
              title="Send Feedback"
            >
              <MessageSquare className="w-5 h-5" />
              <span className="text-sm font-medium">Feedback</span>
            </button>

            {isAuthenticated ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="flex items-center gap-2 text-neutral-300 hover:text-white">
                    <div className="w-8 h-8 rounded-full bg-red-500/20 flex items-center justify-center">
                      <User className="w-4 h-4 text-red-500" />
                    </div>
                    <span className="text-sm font-medium">{user?.name}</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48 bg-[#121212] border-white/10">
                  <DropdownMenuItem asChild>
                    <Link to={getDashboardLink()} className="flex items-center gap-2 cursor-pointer">
                      <LayoutDashboard className="w-4 h-4" />
                      Dashboard
                    </Link>
                  </DropdownMenuItem>
                  {user?.role === 'admin' && (
                    <>
                      <DropdownMenuItem asChild>
                        <Link to="/admin" className="flex items-center gap-2 cursor-pointer">
                          <ShieldCheck className="w-4 h-4" />
                          Admin Panel
                        </Link>
                      </DropdownMenuItem>
                      <DropdownMenuItem asChild>
                        <Link to="/admin/events" className="flex items-center gap-2 cursor-pointer">
                          <Calendar className="w-4 h-4" />
                          Manage Events
                        </Link>
                      </DropdownMenuItem>
                      <DropdownMenuItem asChild>
                        <Link to="/admin/sidequests" className="flex items-center gap-2 cursor-pointer">
                          <Flame className="w-4 h-4" />
                          Manage Quests
                        </Link>
                      </DropdownMenuItem>
                    </>
                  )}
                  <DropdownMenuItem asChild>
                    <Link to="/settings" className="flex items-center gap-2 cursor-pointer">
                      <Settings className="w-4 h-4" />
                      Settings
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator className="bg-white/10" />
                  <DropdownMenuItem onClick={handleLogout} className="flex items-center gap-2 cursor-pointer text-red-400">
                    <LogOut className="w-4 h-4" />
                    Log out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <>
                <Link to="/login">
                  <Button variant="ghost" className="text-neutral-300 hover:text-white">
                    Log in
                  </Button>
                </Link>
                <Link to="/register">
                  <Button className="bg-red-500 hover:bg-red-600 text-white rounded-full px-6">
                    Get Started
                  </Button>
                </Link>
              </>
            )}
          </div>
        </div>
      </header>

      {/* Mobile Header */}
      <header className="md:hidden fixed top-0 left-0 right-0 z-50 glass">
        <div className="px-4 py-3 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <img src={Logo} alt="RydQuest" className="h-8 w-auto" />
            <span className="text-lg font-bold text-white">RydQuest</span>
          </Link>

          <button 
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="p-2 text-white"
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {mobileMenuOpen && (
          <div className="absolute top-full left-0 right-0 glass border-t border-white/10 p-4">
            <nav className="flex flex-col gap-2">
              <Link 
                to="/" 
                onClick={() => setMobileMenuOpen(false)}
                className="px-4 py-3 rounded-lg text-neutral-300 hover:bg-white/5 hover:text-white"
              >
                Home
              </Link>
              <Link 
                to="/events" 
                onClick={() => setMobileMenuOpen(false)}
                className="px-4 py-3 rounded-lg text-neutral-300 hover:bg-white/5 hover:text-white"
              >
                Events
              </Link>
              <Link 
                to="/sidequests" 
                onClick={() => setMobileMenuOpen(false)}
                className="px-4 py-3 rounded-lg text-neutral-300 hover:bg-white/5 hover:text-white"
              >
                Sidequests
              </Link>
              {isAuthenticated ? (
                <>
                  <Link 
                    to={getDashboardLink()} 
                    onClick={() => setMobileMenuOpen(false)}
                    className="px-4 py-3 rounded-lg text-neutral-300 hover:bg-white/5 hover:text-white"
                  >
                    Dashboard
                  </Link>
                  <Link 
                    to="/settings" 
                    onClick={() => setMobileMenuOpen(false)}
                    className="px-4 py-3 rounded-lg text-neutral-300 hover:bg-white/5 hover:text-white flex items-center gap-2"
                  >
                    <Settings className="w-4 h-4" />
                    Settings
                  </Link>
                  <button 
                    onClick={() => { handleLogout(); setMobileMenuOpen(false); }}
                    className="px-4 py-3 rounded-lg text-red-400 hover:bg-white/5 text-left"
                  >
                    Log out
                  </button>
                </>
              ) : (
                <>
                  <Link 
                    to="/login" 
                    onClick={() => setMobileMenuOpen(false)}
                    className="px-4 py-3 rounded-lg text-neutral-300 hover:bg-white/5 hover:text-white"
                  >
                    Log in
                  </Link>
                  <Link 
                    to="/register" 
                    onClick={() => setMobileMenuOpen(false)}
                    className="px-4 py-3 rounded-lg bg-red-500 text-white text-center"
                  >
                    Get Started
                  </Link>
                </>
              )}
            </nav>
          </div>
        )}
      </header>

      {/* Main Content */}
      <main className="pt-16 md:pt-20 pb-24 md:pb-8">
        <Outlet />
      </main>

      {/* Footer with Legal Links */}
      <footer className="hidden md:block border-t border-white/10 bg-[#0A0A0A] py-6">
        <div className="max-w-7xl mx-auto px-6 flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2 text-sm text-neutral-500">
            <span>© 2026 RydQuest. All rights reserved.</span>
          </div>
          <div className="flex items-center gap-6 text-sm">
            <Link to="/terms" className="text-neutral-400 hover:text-white transition-colors">
              Terms of Service
            </Link>
            <Link to="/privacy" className="text-neutral-400 hover:text-white transition-colors">
              Privacy Policy
            </Link>
            <a href="mailto:info@r3vit.com" className="text-neutral-400 hover:text-white transition-colors">
              Contact Us
            </a>
          </div>
        </div>
      </footer>

      <div className="hidden md:block fixed bottom-3 right-4 text-[10px] text-neutral-500/70">
        Build: {BUILD_VERSION}
      </div>

      {/* Mobile Bottom Navigation */}
      <nav className="md:hidden fixed bottom-0 left-0 right-0 glass border-t border-white/10 z-50">
        <div className="flex items-center justify-around py-3 px-4">
          <Link 
            to="/" 
            className={`flex flex-col items-center gap-1 ${
              isActive('/') ? 'text-red-500' : 'text-neutral-500'
            }`}
          >
            <Home className="w-5 h-5" />
            <span className="text-xs">Home</span>
          </Link>
          <Link 
            to="/events" 
            className={`flex flex-col items-center gap-1 ${
              isActive('/events') ? 'text-red-500' : 'text-neutral-500'
            }`}
          >
            <Calendar className="w-5 h-5" />
            <span className="text-xs">Events</span>
          </Link>
          <Link 
            to="/sidequests" 
            className={`flex flex-col items-center gap-1 ${
              location.pathname.startsWith('/sidequests') ? 'text-orange-500' : 'text-neutral-500'
            }`}
          >
            <Flame className="w-5 h-5" />
            <span className="text-xs">Quests</span>
          </Link>
          {isAuthenticated ? (
            <Link 
              to={getDashboardLink()} 
              className={`flex flex-col items-center gap-1 ${
                location.pathname.includes('/dashboard') || location.pathname.includes('/admin') 
                  ? 'text-red-500' 
                  : 'text-neutral-500'
              }`}
            >
              <LayoutDashboard className="w-5 h-5" />
              <span className="text-xs">Dashboard</span>
            </Link>
          ) : (
            <Link 
              to="/login" 
              className={`flex flex-col items-center gap-1 ${
                isActive('/login') ? 'text-red-500' : 'text-neutral-500'
              }`}
            >
              <User className="w-5 h-5" />
              <span className="text-xs">Account</span>
            </Link>
          )}
        </div>
      </nav>

      {/* Feedback Modal */}
      <FeedbackModal isOpen={feedbackOpen} onClose={() => setFeedbackOpen(false)} />
    </div>
  );
};

export default Layout;
